import { t } from '@muraletta/i18n';
import { httpsUrl, editImage, editQuota, frameImages, readCreation, saveCreation, STORAGE_KEY } from '@muraletta/studio-api';
import { setupWorkbench } from '@muraletta/workbench';

class Personalizer extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;
    this.find = selector => this.querySelector(selector);
    this.custom = this.dataset.customProduct === 'true';
    this.key = this.custom ? STORAGE_KEY : `muraletta_product_${this.dataset.productId}`;
    const base = httpsUrl(new URL(this.dataset.baseImage, location.origin).href);
    this.record = readCreation(this.key) || { title: this.dataset.title, mode: 'edit', selectedIndex: 0, versions: this.custom ? [] : [{ url: base, original: true }] };
    this.originalImages = new Map(); this.frameCache = {}; this.frameVersion = '';
    this.dialog = this.find('dialog');
    this.workbench = setupWorkbench(this.dialog);
    this.find('[data-compare]').addEventListener('click', () => {
      this.find('[data-editor-frame]').value = 'none'; this.workbench.syncFrames();
      this.comparing = !this.comparing;
      this.find('[data-compare]').setAttribute('aria-pressed', String(this.comparing));
      this.find('[data-compare]').textContent = this.comparing ? t('Voir ma version') : t('Voir l’original');
      this.find('[data-editor-image]').src = this.comparing ? this.record.versions[0].url : this.draft.url;
    });
    this.find('[data-open-editor]').addEventListener('click', () => this.open());
    this.find('[data-close-editor]').addEventListener('click', () => this.dialog.close());
    this.find('[data-edit-form]').addEventListener('submit', event => { event.preventDefault(); this.edit(); });
    this.find('[data-apply-version]').addEventListener('click', () => this.apply());
    this.find('[data-editor-frame]').addEventListener('change', () => this.previewFrame());
    this.submitGuard = event => {
      if (!this.forms().includes(event.target)) return;
      if (!this.canPurchase()) { event.preventDefault(); event.stopImmediatePropagation(); this.notice(); }
      else this.sync();
    };
    document.addEventListener('submit', this.submitGuard, true);
    this.changeHandler = () => this.scheduleSync();
    document.addEventListener('change', this.changeHandler);
    this.observer = new MutationObserver(() => this.scheduleSync());
    this.observer.observe(document.querySelector('main') || document.body, { childList: true, subtree: true });
    this.renderVersions(); this.sync();
    if (new URLSearchParams(location.search).get('personnaliser') === '1') this.open();
  }
  disconnectedCallback() {
    this.observer?.disconnect();
    this.workbench?.destroy();
    document.removeEventListener('submit', this.submitGuard, true);
    document.removeEventListener('change', this.changeHandler);
  }
  current() { return this.record.versions[this.record.selectedIndex]; }
  forms() { return [...document.querySelectorAll(`product-form-component[data-product-id="${this.dataset.productId}"] form[action*="/cart/add"]`)]; }
  canPurchase() { return this.current()?.original || (this.dataset.orderingEnabled === 'true' && this.current() && (this.current().creationId || this.record.creationId)); }
  persist() { try { saveCreation(this.record, this.key); } catch { /* Current page still works without storage. */ } }
  notice() {
    const notice = this.find('[data-purchase-notice]');
    notice.hidden = Boolean(this.canPurchase());
    const message = this.custom && !this.current() ? t('Créez votre affiche dans le Studio avant de continuer.') : t('Votre version personnalisée est conservée. Sa commande sera ouverte après validation du circuit d’impression. Sélectionnez Original pour acheter l’affiche du catalogue.');
    if (notice.textContent !== message) notice.textContent = message;
  }
  scheduleSync() {
    if (this.syncScheduled) return;
    this.syncScheduled = true;
    queueMicrotask(() => { this.syncScheduled = false; if (this.isConnected) this.sync(); });
  }
  sync() {
    const current = this.current();
    const customized = current && !current.original;
    const allowed = this.canPurchase();
    for (const form of this.forms()) {
      const properties = customized ? {
        _apercu: current.url, _design: current.url,
        Personnalisation: `${this.record.title} — version ${this.record.selectedIndex + 1}`,
        _afich_creation_id: current.creationId || this.record.creationId || '',
        _creationId: current.creationId || this.record.creationId || '',
        _origine: 'studio', _marque: 'MURALETTA',
      } : {};
      form.querySelectorAll('[data-muraletta-property]').forEach(input => { if (!(input.dataset.muralettaProperty in properties)) input.remove(); });
      Object.entries(properties).forEach(([key, value]) => {
        let input = form.querySelector(`[data-muraletta-property="${key}"]`);
        if (!input) { input = document.createElement('input'); input.type = 'hidden'; input.name = `properties[${key}]`; input.dataset.muralettaProperty = key; form.append(input); }
        input.value = value;
      });
      const component = form.closest('product-form-component');
      component.querySelectorAll('button[name="add"], [ref="addToCartButton"]').forEach(button => {
        if (!allowed) {
          if (!button.hasAttribute('data-muraletta-blocked')) button.dataset.muralettaBlocked = String(button.disabled);
          button.disabled = true;
        } else if (button.hasAttribute('data-muraletta-blocked')) {
          button.disabled = button.dataset.muralettaBlocked === 'true'; delete button.dataset.muralettaBlocked;
        }
      });
      component.querySelectorAll('.accelerated-checkout-block, shopify-accelerated-checkout').forEach(element => {
        if (customized || this.custom) { element.hidden = true; element.dataset.muralettaHidden = 'true'; }
        else if (element.dataset.muralettaHidden) { element.hidden = false; delete element.dataset.muralettaHidden; }
      });
    }
    // The base theme also has a sticky add button outside its main product form.
    document.querySelectorAll('sticky-add-to-cart button').forEach(button => {
      if (!allowed) { if (!button.hasAttribute('data-muraletta-blocked')) button.dataset.muralettaBlocked = String(button.disabled); button.disabled = true; }
      else if (button.hasAttribute('data-muraletta-blocked')) { button.disabled = button.dataset.muralettaBlocked === 'true'; delete button.dataset.muralettaBlocked; }
    });
    this.notice(); this.updateGallery();
  }
  renderVersions() {
    const list = this.find('[data-versions]'); list.replaceChildren();
    this.record.versions.forEach((version, index) => {
      const button = document.createElement('button'); button.type = 'button'; button.setAttribute('aria-pressed', String(index === this.record.selectedIndex));
      const image = document.createElement('img'); image.src = version.url; image.alt = '';
      const label = document.createElement('span'); label.textContent = version.original ? t('Original') : t('Version {number}', {number:index + 1});
      button.append(image, label); button.addEventListener('click', () => { this.record.selectedIndex = index; this.renderVersions(); this.persist(); this.sync(); }); list.append(button);
    });
  }
  open() {
    if (!this.current()) return this.notice();
    if (this.busy) { this.dialog.showModal(); return; }
    if (!this.hasUnappliedChanges) {
      this.editorVersions = this.record.versions.map(version => ({ ...version }));
      this.draft = this.editorVersions[this.record.selectedIndex];
    }
    this.showDraft();
    this.find('[data-editor-frame]').value = 'none'; this.find('[data-editor-status]').textContent = '';
    this.find('[name="instruction"]').value = ''; this.setBusy(false);
    this.workbench.activate('edit'); this.workbench.syncFrames(); this.renderEditorVersions();
    this.dialog.showModal();
  }
  showDraft() {
    this.comparing = false; this.find('[data-compare]').setAttribute('aria-pressed', 'false'); this.find('[data-compare]').textContent = t('Voir l’original');
    this.find('[data-editor-image]').src = this.draft.url;
    this.find('[data-editor-frame]').value = 'none'; this.workbench.syncFrames();
    this.find('[data-version-label]').textContent = this.draft.original ? t('Original · votre point de départ') : t('Votre création · version {number}', {number:this.editorVersions.indexOf(this.draft) + 1});
  }
  renderEditorVersions() {
    const list = this.find('[data-editor-versions]'); list.replaceChildren();
    this.editorVersions.forEach((version, index) => {
      const button = document.createElement('button'); button.type = 'button'; button.setAttribute('aria-pressed', String(version === this.draft));
      const image = document.createElement('img'); image.src = version.url; image.alt = '';
      const label = document.createElement('span'); label.textContent = version.original ? t('Original') : t('Version {number}', {number:index + 1});
      button.append(image, label); button.addEventListener('click', () => { this.draft = version; this.showDraft(); this.renderEditorVersions(); }); list.append(button);
    });
  }
  setBusy(busy) {
    this.busy = busy;
    this.dialog.querySelectorAll('button,textarea,select').forEach(element => { if (!element.matches('[data-close-editor]')) element.disabled = busy; });
    const quota = editQuota(); this.find('[data-quota]').textContent = quota.remaining ? t('{count}/5 retouches restantes · renouvelées sous 24 h', {count:quota.remaining}) : t('Limite atteinte. Réessayez dans {hours} h.', {hours:quota.hours});
    this.find('[data-edit-form] button[type="submit"]').disabled = busy || !quota.remaining;
    this.dialog.setAttribute('aria-busy', String(busy));
  }
  async edit() {
    if (this.busy || !this.draft) return;
    this.setBusy(true); const status = this.find('[data-editor-status]'); status.textContent = t('L’IA retouche votre affiche…');
    try {
      const result = await editImage(this.draft.url, this.find('[name="instruction"]').value);
      this.draft = { ...result, creationId: result.creationId || this.draft.creationId || this.record.creationId || '' };
      this.editorVersions.push(this.draft); this.hasUnappliedChanges = true; this.showDraft(); this.renderEditorVersions();
      status.textContent = t('Votre retouche est prête. À vous de choisir !');
    } catch (error) { status.textContent = error.message; }
    finally { this.setBusy(false); }
  }
  async previewFrame() {
    if (!this.draft || this.busy) return;
    const frame = this.find('[data-editor-frame]').value;
    this.comparing = false; this.find('[data-compare]').setAttribute('aria-pressed', 'false'); this.find('[data-compare]').textContent = t('Voir l’original');
    if (frame === 'none') { this.find('[data-editor-image]').src = this.draft.url; return; }
    this.setBusy(true);
    try {
      const frames = await frameImages(this.draft.url);
      if (!frames[frame]) throw new Error(t('Cet aperçu n’est pas disponible.'));
      this.find('[data-editor-image]').src = frames[frame]; this.frameCache[this.draft.url] = frames;
      this.find('[data-editor-status]').textContent = t('Aperçu de mise en situation, non contractuel.');
    } catch (error) { this.find('[data-editor-status]').textContent = error.message; }
    finally { this.setBusy(false); }
  }
  apply() {
    if (this.busy || !this.draft) return;
    this.record.versions = this.editorVersions;
    this.record.selectedIndex = this.editorVersions.indexOf(this.draft);
    this.hasUnappliedChanges = false;
    this.persist(); this.renderVersions(); this.sync(); this.dialog.close();
  }
  updateGallery() {
    const version = this.current(); if (!version) return;
    const gallery = document.querySelector('main media-gallery'); if (!gallery) return;
    let frame = 'none';
    const inputs = [...document.querySelectorAll('main variant-picker input:checked')].map(input => input.value.toLowerCase());
    if (inputs.some(value => value.includes('bois') || value === t('Cadre bois').toLowerCase())) frame = 'wood';
    else if (inputs.some(value => value.includes('blanc') || value === t('Cadre blanc').toLowerCase())) frame = 'white';
    else if (inputs.some(value => value.includes('noir') || value === t('Cadre noir').toLowerCase())) frame = 'black';
    const url = version.original ? '' : (this.frameCache[version.url]?.[frame] || version.url);
    gallery.querySelectorAll('img').forEach(image => {
      if (!this.originalImages.has(image)) this.originalImages.set(image, { src: image.getAttribute('src'), srcset: image.getAttribute('srcset'), max: image.getAttribute('data_max_resolution') });
      const original = this.originalImages.get(image);
      if (url) { image.removeAttribute('srcset'); if (image.getAttribute('src') !== url) image.src = url; image.setAttribute('data_max_resolution', url); }
      else { if (original.srcset) image.setAttribute('srcset', original.srcset); if (image.getAttribute('src') !== original.src) image.setAttribute('src', original.src); if (original.max) image.setAttribute('data_max_resolution', original.max); }
    });
    if (!version.original && frame !== 'none' && !this.frameCache[version.url] && this.frameVersion !== version.url) {
      this.frameVersion = version.url;
      frameImages(version.url).then(frames => { this.frameCache[version.url] = frames; this.updateGallery(); }).catch(() => {});
    }
  }
}
if (!customElements.get('muraletta-personalizer')) customElements.define('muraletta-personalizer', Personalizer);
